import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Organization, useOrganization } from '../../contexts/OrganizationContext';
import { Spinner } from '../../design-system';
import toast from '../../design-system/toast';
import OrgFindingClosureSetting from './OrgFindingClosureSetting';

interface OrgGeneralSectionProps {
  organization: Organization;
}

const OrgGeneralSection: React.FC<OrgGeneralSectionProps> = ({ organization }) => {
  const { updateOrganization, refreshOrganizations, isAdmin } = useOrganization();

  const [name, setName] = useState(organization.name);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state when organization prop changes
  useEffect(() => {
    setName(organization.name);
  }, [organization.name]);

  const hasChanges = name.trim() !== organization.name;
  const isValid = name.trim().length >= 1;

  const handleSave = async () => {
    if (!hasChanges || !isValid) return;

    try {
      setIsSaving(true);
      await updateOrganization(organization.id, { name: name.trim() });
      await refreshOrganizations();
      toast.success('Organization name updated');
    } catch (err: any) {
      console.error('Failed to update organization:', err);
      const errorMessage = err.data?.detail || err.message || 'Failed to update organization';
      toast.error(errorMessage);
      setName(organization.name);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setName(organization.name);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-ui-text-primary">
          General Settings
        </h3>
        <p className="text-sm text-ui-text-secondary mt-1">
          Manage your organization's basic information.
        </p>
      </div>

      <div className="bg-ui-background-tertiary rounded-lg border border-ui-border-primary p-6">
        <div className="space-y-4">
          <div>
            <label
              htmlFor="orgName"
              className="block text-sm font-medium text-ui-text-secondary mb-2"
            >
              Organization Name
            </label>
            <input
              type="text"
              id="orgName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isAdmin || isSaving}
              maxLength={100}
              className="w-full px-4 py-2 border border-ui-border-primary rounded-lg 
                         bg-ui-background-secondary text-ui-text-primary
                         placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                         focus:ring-2 focus:ring-brand-primary focus:border-transparent
                         disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {!isAdmin && (
              <p className="mt-2 text-xs text-ui-text-tertiary">
                Only admins and owners can edit the organization name.
              </p>
            )}
          </div>

          {isAdmin && hasChanges && (
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving || !isValid}
                className="px-4 py-2 bg-brand-primary hover:bg-brand-primaryDark disabled:opacity-50 
                           text-black font-medium rounded-lg transition-colors
                           flex items-center gap-2 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Spinner size={16} tone="current" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 text-ui-text-secondary hover:text-ui-text-primary 
                           dark:hover:text-white font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <OrgFindingClosureSetting organization={organization} />
    </div>
  );
};

export default OrgGeneralSection;
